import type { Map as ImmutableMap } from "immutable"

import { checkPermission } from "shared/imports/permissionFunctions"
import { stateRegions } from "shared/imports/regionConstants"

const { QuorumDataType } = DjangIO.app.models
const { EventType } = DjangIO.app.events.models

export function checkAiChatConditions(): boolean {
    // Do they have the Copilot Permission?
    const hasCopilot = checkPermission(DjangIO.app.models.PermissionType.copilot_analyze)

    // Are they also individually allowed to view the Federal/State Bill Profiles?
    const hasIndividualRegionPermissions = checkPermission(DjangIO.app.models.PermissionType.legislative_profiles, {
        regions: [
            DjangIO.app.models.SupportedRegion.federal.value,
            ...(DjangIO.app.models.SupportedRegion.states.child_regions as unknown as number[]),
        ],
        useCurrentRegions: true,
    })

    return hasCopilot && hasIndividualRegionPermissions
}

/**
 * Should AI Chat input should be enabled.
 *
 * This differs from checkCoPilotAnalyzeConditions in that the sidebar is already rendered, but the user can't ask any
 * questions until they're on a Bill or Committee Meeting profile.
 *
 * @param dataType - QuorumDataType key matched from the URL (:dataType/:profileId/*)
 * @param profileId - Profile ID matched from the URL (:dataType/:profileId/*)
 * @param billProfile - Bill profile data from the Redux store
 * @param eventProfile - Event profile data from the Redux store
 *
 * @returns boolean
 */
export const isChatEnabled = (
    dataType: string,
    profileId: string,
    billProfile: ImmutableMap<string, object>,
    eventProfile: ImmutableMap<string, object>,
) => {
    if (!checkPermission(DjangIO.app.models.PermissionType.copilot_analyze)) {
        return false
    }

    switch (dataType) {
        // Enable AI Chat when the user is on a Federal or State Bill profile
        case QuorumDataType.bill.key:
            const billRegion = billProfile?.getIn([`${profileId}`, "bill"])?.region
            const isFederalBill = billRegion === DjangIO.app.models.Region.federal.value
            const isStateBill = stateRegions.includes(billRegion)

            return isFederalBill || isStateBill

        // Enable AI Chat when the user is on an Event Profile, and the Event is a Committee Meeting
        case QuorumDataType.event.key:
            const eventType = eventProfile?.getIn([`${profileId}`, "event", "event_type"])
            const isCommitteeTranscript = eventType === EventType.committee_meeting.value

            return isCommitteeTranscript

        // Otherwise, no AI Chat
        default:
            return false
    }
}
